export const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es').trim().replace(/\s+/g, ' ');

export function selectWorks(items, { category = '', state = '', search = '', order = 'newest' } = {}) {
  const terms = normalize(search).split(' ').filter(Boolean);
  return items.filter(item => (!category || normalize(item.category) === category) && (!state || item.state === state) && terms.every(term => normalize([item.name, item.product?.title, item.finish, item.upholstery].join(' ')).includes(term))).sort((a, b) => {
    if (order === 'name') return a.name.localeCompare(b.name, 'es', { sensitivity: 'base', numeric: true });
    const comparison = String(a.id).localeCompare(String(b.id), 'en', { numeric: true });
    return order === 'oldest' ? comparison : -comparison;
  });
}

export function pageWorks(items, page, size = 12) {
  const pages = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(pages, Math.max(1, Number(page) || 1));
  return { items: items.slice((current - 1) * size, current * size), page: current, pages };
}

export function validWork(item) {
  return item && typeof item.handle === 'string' && typeof item.name === 'string' && item.name.trim() && typeof item.category === 'string' && item.category.trim() && ['Terminado', 'En proceso'].includes(item.state) && Array.isArray(item.images) && item.images.length > 0 && item.images.every(image => image && typeof image.src === 'string' && /^(https:\/\/|\/\/)/.test(image.src) && typeof image.thumb === 'string' && /^(https:\/\/|\/\/)/.test(image.thumb));
}

export async function collectIndex(initial, fetchPage, signal) {
  const records = new Map();
  const seen = new Set();
  let chunk = initial;
  while (chunk) {
    if (!Array.isArray(chunk.items) || !Number.isInteger(chunk.page) || seen.has(chunk.page)) throw new Error('Invalid archive page');
    seen.add(chunk.page);
    for (const item of chunk.items) if (validWork(item)) records.set(item.handle, item);
    if (!chunk.next) break;
    if (seen.size >= 250) throw new Error('Archive requires a server search index');
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    chunk = await fetchPage(chunk.next, signal);
  }
  return [...records.values()];
}

export function workLink(base, handle, previewThemeId = '') {
  const url = new URL(base);
  for (const key of ['categoria', 'estado', 'buscar', 'orden', 'archivo_pagina', 'page', 'section_id', 'sections', 'pb', 'preview_theme_id']) url.searchParams.delete(key);
  if (/^\d+$/.test(previewThemeId)) url.searchParams.set('preview_theme_id', previewThemeId);
  url.hash = new URLSearchParams({ trabajo: handle }).toString();
  return url.href;
}

export function consultationLink(phone, template, item, url) {
  const message = template.replace('[nombre]', item.name).replace('[identificador]', item.handle).replace('[enlace]', url);
  return `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
}

function createNode(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class RealWorkArchive {
  constructor(root) {
    this.root = root;
    this.$ = selector => root.querySelector(selector);
    this.labels = JSON.parse(this.$('[data-rw-labels]').textContent);
    this.initial = JSON.parse(this.$('[data-rw-data]').textContent);
    this.form = this.$('[data-rw-filters]');
    this.filterViewport = window.matchMedia('(min-width: 600px)');
    const updateFilterPanel = () => { this.$('[data-rw-more]').open = this.filterViewport.matches; };
    updateFilterPanel();
    this.dialog = this.$('[data-rw-dialog]');
    this.controller = new AbortController();
    this.filterViewport.addEventListener('change', updateFilterPanel, { signal: this.controller.signal });
    this.items = [];
    this.currentPage = Math.max(1, Number(new URL(location.href).searchParams.get('archivo_pagina')) || 1);
    const params = new URL(location.href).searchParams;
    this.pendingCategory = params.get('categoria') || '';
    this.form.elements.state.value = params.get('estado') || '';
    this.form.elements.search.value = (params.get('buscar') || '').slice(0, 120);
    this.form.elements.order.value = ['newest', 'oldest', 'name'].includes(params.get('orden')) ? params.get('orden') : 'newest';
    this.form.addEventListener('submit', event => event.preventDefault(), { signal: this.controller.signal });
    this.form.addEventListener('input', () => { this.currentPage = 1; this.render(); }, { signal: this.controller.signal });
    this.form.addEventListener('reset', () => setTimeout(() => { if (this.controller.signal.aborted) return; this.currentPage = 1; this.render(); }, 0), { signal: this.controller.signal });
    this.on('[data-rw-previous]', 'click', () => this.movePage(-1));
    this.on('[data-rw-next]', 'click', () => this.movePage(1));
    this.on('[data-rw-retry]', 'click', () => this.load());
    this.on('[data-rw-close]', 'click', () => this.dialog.close());
    this.on('[data-rw-photo-previous]', 'click', () => this.showPhoto(-1));
    this.on('[data-rw-photo-next]', 'click', () => this.showPhoto(1));
    this.on('[data-rw-share]', 'click', () => this.share());
    this.dialog.addEventListener('close', () => this.cleanupDialog(), { signal: this.controller.signal });
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, { signal: this.controller.signal });
    this.dialog.addEventListener('keydown', event => {
      if (event.target.matches('input,textarea,select')) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.showPhoto(event.key === 'ArrowRight' ? 1 : -1); }
    }, { signal: this.controller.signal });
    this.on('[data-rw-stage]', 'touchstart', event => { this.touchX = event.touches.length === 1 ? event.touches[0].clientX : null; this.touchY = event.touches.length === 1 ? event.touches[0].clientY : null; }, { passive: true });
    this.on('[data-rw-stage]', 'touchend', event => {
      if (this.touchX === null || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - this.touchX;
      const dy = event.changedTouches[0].clientY - this.touchY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.showPhoto(dx < 0 ? 1 : -1);
      this.touchX = null;
    }, { passive: true });
    window.addEventListener('hashchange', () => this.openFromHash(), { signal: this.controller.signal });
    this.load();
  }

  on(selector, event, callback, options = {}) { this.$(selector).addEventListener(event, callback, { ...options, signal: this.controller.signal }); }

  async load() {
    this.$('[data-rw-retry]').hidden = true;
    this.$('[data-rw-status]').textContent = this.labels.loading;
    for (const input of this.form.elements) input.disabled = true;
    try {
      const fetchChunk = async next => {
        const url = new URL(location.href);
        const nextPage = new URL(next, location.origin).searchParams.get('page');
        if (!nextPage) throw new Error('Missing archive page');
        url.searchParams.set('page', nextPage);
        url.searchParams.set('section_id', this.root.dataset.sectionId);
        url.hash = '';
        const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(20000)]) });
        if (!response.ok) throw new Error('Archive unavailable');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = document.querySelector('[data-rw-data]');
        if (!data) throw new Error('Missing archive data');
        return JSON.parse(data.textContent);
      };
      const initial = this.initial.page === 1 ? this.initial : await fetchChunk('?page=1');
      this.items = await collectIndex(initial, fetchChunk, this.controller.signal);
      const category = this.form.elements.category;
      category.replaceChildren(new Option(this.labels.all_categories, ''));
      const categories = new Map();
      this.items.forEach(item => { const key = normalize(item.category); if (!categories.has(key)) categories.set(key, item.category.trim()); });
      [...categories].sort((a, b) => a[1].localeCompare(b[1], 'es')).forEach(([key, label]) => category.add(new Option(label, key)));
      category.value = normalize(this.pendingCategory);
      for (const input of this.form.elements) input.disabled = false;
      this.ready = true;
      this.render();
      this.openFromHash();
    } catch (error) {
      if (this.controller.signal.aborted) return;
      this.ready = false;
      this.$('[data-rw-status]').textContent = this.labels.error;
      this.$('[data-rw-retry]').hidden = false;
    }
  }

  render() {
    const criteria = Object.fromEntries(new FormData(this.form));
    this.pendingCategory = criteria.category;
    const extraFilters = Number(Boolean(criteria.search)) + Number(criteria.order !== 'newest');
    this.$('[data-rw-more-label]').textContent = this.labels.more_filters + (extraFilters ? ` (${extraFilters})` : '');
    const filtered = selectWorks(this.items, criteria);
    const page = pageWorks(filtered, this.currentPage);
    this.currentPage = page.page;
    const fragment = document.createDocumentFragment();
    for (const item of page.items) {
      const card = createNode('article', 'rw__card');
      const button = createNode('button', 'rw__card-button');
      button.type = 'button';
      const portrait = item.images[0].height > item.images[0].width;
      const media = createNode('div', `rw__card-media${portrait ? ' rw__card-media--portrait' : ''}`);
      const image = createNode('img', 'rw__card-image');
      const photo = item.images[0];
      image.alt = photo.alt || item.name;
      image.width = photo.width;
      image.height = photo.height;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.src = photo.thumb;
      const badge = createNode('span', `rw__badge${item.state === 'En proceso' ? ' rw__badge--process' : ''}`, item.state === 'En proceso' ? this.labels.in_progress : this.labels.finished);
      media.append(image, badge);
      button.append(media, createNode('h2', '', item.name), createNode('p', '', [item.finish, item.upholstery].filter(Boolean).join(' · ')), createNode('p', '', `${item.images.length} ${item.images.length === 1 ? this.labels.photo : this.labels.photos}`), createNode('span', 'rw__card-link', this.labels.open));
      button.addEventListener('click', () => this.open(item, button));
      card.append(button);
      fragment.append(card);
    }
    this.$('[data-rw-grid]').replaceChildren(fragment);
    this.$('[data-rw-status]').textContent = this.items.length === 0 ? this.labels.empty : filtered.length === 0 ? this.labels.no_results : `${filtered.length} ${this.labels.results}`;
    this.$('[data-rw-pagination]').hidden = page.pages <= 1;
    this.$('[data-rw-page]').textContent = `${this.labels.page} ${page.page} ${this.labels.of} ${page.pages}`;
    this.$('[data-rw-previous]').disabled = page.page === 1;
    this.$('[data-rw-next]').disabled = page.page === page.pages;
    this.syncUrl(criteria);
  }

  syncUrl(criteria) {
    const url = new URL(location.href);
    const values = { categoria: criteria.category, estado: criteria.state, buscar: criteria.search, orden: criteria.order === 'newest' ? '' : criteria.order, archivo_pagina: this.currentPage > 1 ? this.currentPage : '' };
    for (const [key, value] of Object.entries(values)) value ? url.searchParams.set(key, value) : url.searchParams.delete(key);
    history.replaceState(null, '', url);
  }

  movePage(direction) {
    this.currentPage += direction;
    this.render();
    this.$('[data-rw-status]').scrollIntoView({ block: 'start' });
    const first = this.$('[data-rw-grid] button');
    first?.focus({ preventScroll: true });
  }

  openFromHash() {
    if (!this.ready) return;
    const params = new URLSearchParams(location.hash.slice(1));
    const handle = params.get('trabajo');
    if (!handle) { if (this.dialog.open) this.dialog.close(); return; }
    const item = this.items.find(work => work.handle === handle);
    if (item) this.open(item, document.activeElement, false);
    else this.$('[data-rw-status]').textContent = this.labels.not_found;
  }

  open(item, opener, updateUrl = true) {
    this.active = item;
    this.opener = opener;
    this.photoIndex = 0;
    this.$('[data-rw-title]').textContent = item.name;
    this.$('[data-rw-category]').textContent = item.category;
    const badge = this.$('[data-rw-badge]');
    badge.textContent = item.state === 'En proceso' ? this.labels.in_progress : this.labels.finished;
    badge.classList.toggle('rw__badge--process', item.state === 'En proceso');
    for (const [selector, value] of [['finish', item.finish], ['upholstery', item.upholstery]]) {
      const element = this.$(`[data-rw-${selector}]`);
      element.hidden = !value;
      element.textContent = value ? `${this.labels[selector]}: ${value}` : '';
    }
    this.$('[data-rw-notes]').textContent = item.notes || '';
    this.$('[data-rw-notes]').hidden = !item.notes;
    this.$('[data-rw-process]').hidden = item.state !== 'En proceso';
    const link = this.$('[data-rw-product]');
    const productUrl = item.product?.url;
    link.hidden = !productUrl || !productUrl.startsWith('/products/');
    if (!link.hidden) link.href = productUrl;
    else link.removeAttribute('href');
    this.$('[data-rw-share-status]').replaceChildren();
    this.$('[data-rw-consult]').href = consultationLink(this.root.dataset.whatsappPhone, this.labels.whatsapp_message, item, workLink(location.href, item.handle, this.root.dataset.previewThemeId));
    this.showPhoto(0);
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.classList.add('rw__dialog-open');
    this.$('[data-rw-close]').focus();
    if (updateUrl) { const url = new URL(location.href); url.hash = new URLSearchParams({ trabajo: item.handle }); history.pushState(null, '', url); }
  }

  showPhoto(direction) {
    if (!this.active) return;
    this.photoIndex = (this.photoIndex + direction + this.active.images.length) % this.active.images.length;
    const photo = this.active.images[this.photoIndex];
    const image = this.$('[data-rw-image]');
    image.src = photo.src;
    image.alt = photo.alt || this.active.name;
    image.width = photo.width;
    image.height = photo.height;
    this.$('[data-rw-original]').href = photo.src;
    this.$('[data-rw-counter]').textContent = `${this.photoIndex + 1} / ${this.active.images.length}`;
    this.$('[data-rw-photo-previous]').hidden = this.active.images.length === 1;
    this.$('[data-rw-photo-next]').hidden = this.active.images.length === 1;
  }

  cleanupDialog() {
    document.documentElement.classList.remove('rw__dialog-open');
    this.$('[data-rw-image]').removeAttribute('src');
    this.active = null;
    const url = new URL(location.href);
    if (new URLSearchParams(url.hash.slice(1)).has('trabajo')) { url.hash = ''; history.replaceState(null, '', url); }
    if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
  }

  async share() {
    const url = workLink(location.href, this.active.handle, this.root.dataset.previewThemeId);
    const status = this.$('[data-rw-share-status]');
    try {
      if (navigator.share) await navigator.share({ title: this.active.name, url });
      else { await navigator.clipboard.writeText(url); status.textContent = this.labels.copied; }
    } catch (error) {
      if (error.name === 'AbortError') return;
      status.textContent = this.labels.share_failed;
      const input = createNode('input', 'rw__share-link');
      input.readOnly = true;
      input.value = url;
      input.setAttribute('aria-label', this.labels.share_failed);
      status.append(input);
      input.select();
    }
  }

  destroy() {
    this.controller.abort();
    if (this.dialog.open) this.dialog.close();
    document.documentElement.classList.remove('rw__dialog-open');
  }
}

if (typeof document !== 'undefined') {
  const instances = new Map();
  const initialize = scope => scope.querySelectorAll('[data-real-work-archive]').forEach(root => {
    if (!instances.has(root)) instances.set(root, new RealWorkArchive(root));
  });
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, instance] of instances) if (event.target.contains(root)) { instance.destroy(); instances.delete(root); }
  });
}
